import { Box, CircularProgress, Typography, useTheme } from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import AddIcon from "@mui/icons-material/Add"
import React, { useState } from "react"
import { useDispatch } from "react-redux"
import AppTextField from "components/StyledComponents/AppTextField"
import AppButton from "components/StyledComponents/AppButton"
import StyledLabel from "components/StyledComponents/StyledLabel"
import CheckBox from "components/CheckBox"
import { searchDidYouKnowImagesAction, confirmDidYouKnowImageAction } from "redux/marketing/actions"

/**
 * Search the web for an image and pick one for an image field.
 *
 * Renders a search bar, a horizontal-scroll strip of result thumbnails and a
 * Confirm button. On confirm the chosen image is downloaded + stored server
 * side and `onConfirm(media)` is called with `{ id, url }` so the parent can
 * write it into form state exactly like an uploaded image.
 */
const ImageSearchField = ({
  label = "Search Image",
  placeholder = "Search the web for an image",
  count = 10,
  disabled = false,
  onConfirm = () => {}
}) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const [query, setQuery] = useState("")
  const [results, setResults] = useState([])
  const [selectedUrl, setSelectedUrl] = useState("")
  const [loading, setLoading] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [searched, setSearched] = useState(false)
  // Server-side pagination: each page is one request (Google CSE returns 10 at a
  // time, capped server-side at 50 total). "Show more" fetches the next page.
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  const handleSearch = () => {
    const trimmed = query.trim()
    if (!trimmed || loading) return
    setLoading(true)
    setSelectedUrl("")
    setResults([])
    setPage(1)
    setHasMore(false)
    dispatch(
      searchDidYouKnowImagesAction({
        query: trimmed,
        count,
        page: 1,
        onSuccess: ({ results: pageResults, hasMore: more }) => {
          setResults(pageResults)
          setHasMore(more)
          setPage(1)
          setSearched(true)
          setLoading(false)
        },
        onError: () => {
          setResults([])
          setHasMore(false)
          setSearched(true)
          setLoading(false)
        }
      })
    )
  }

  const handleLoadMore = () => {
    if (loadingMore || !hasMore) return
    const nextPage = page + 1
    setLoadingMore(true)
    dispatch(
      searchDidYouKnowImagesAction({
        query: query.trim(),
        count,
        page: nextPage,
        onSuccess: ({ results: pageResults, hasMore: more }) => {
          // Append, de-duping by url in case a page overlaps.
          setResults((prev) => {
            const seen = new Set(prev.map((r) => r?.url))
            return [...prev, ...pageResults.filter((r) => r?.url && !seen.has(r.url))]
          })
          setHasMore(more)
          setPage(nextPage)
          setLoadingMore(false)
        },
        onError: () => setLoadingMore(false)
      })
    )
  }

  const handleConfirm = () => {
    if (!selectedUrl || confirming) return
    setConfirming(true)
    dispatch(
      confirmDidYouKnowImageAction({
        url: selectedUrl,
        onSuccess: (media) => {
          setConfirming(false)
          onConfirm(media)
          setResults([])
          setSelectedUrl("")
          setSearched(false)
          setQuery("")
        },
        onError: () => {
          setConfirming(false)
        }
      })
    )
  }

  return (
    <Box>
      {label ? <StyledLabel variant="body1Regular">{label}</StyledLabel> : null}
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Box sx={{ flexGrow: 1 }}>
          <AppTextField
            id="image-search-query"
            name="image-search-query"
            type="text"
            fullWidth
            minRows={1}
            maxRows={1}
            placeholder={placeholder}
            value={query}
            isDisabled={disabled || loading}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault()
                handleSearch()
              }
            }}
          />
        </Box>
        <AppButton
          styles={{
            height: "50px",
            padding: "0 16px",
            fontSize: "13px",
            whiteSpace: "nowrap",
            minWidth: "132px",
            justifyContent: "center"
          }}
          disabled={disabled || loading || !query.trim()}
          onClick={handleSearch}
          startIcon={<SearchIcon />}>
          Search
        </AppButton>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={28} sx={{ color: theme.palette.secondary.main }} />
        </Box>
      ) : null}

      {!loading && searched && results.length === 0 ? (
        <Typography
          variant="body2Regular"
          sx={{ display: "block", mt: 2, color: theme.palette.secondary.main }}>
          No images found. Try a different search.
        </Typography>
      ) : null}

      {!loading && results.length > 0 ? (
        <>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              overflowX: "auto",
              mt: 2,
              pb: 1
            }}>
            {results.map((img, index) => {
              const isChecked = selectedUrl === img?.url
              return (
                <Box
                  key={`${img?.url}-${index}`}
                  onClick={() => setSelectedUrl((prev) => (prev === img?.url ? "" : img?.url))}
                  sx={{
                    position: "relative",
                    flexShrink: 0,
                    width: 200,
                    height: 200,
                    cursor: "pointer",
                    borderRadius: "12px",
                    padding: "4px",
                    backgroundColor: theme.palette.background.main,
                    border: isChecked
                      ? `2px solid ${theme.palette.secondary.main}`
                      : `2px solid ${theme.palette.background.main}`
                  }}>
                  <img
                    src={img?.url}
                    alt={`search-result-${index}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "8px",
                      aspectRatio: "1",
                      objectFit: "cover"
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      left: 8,
                      zIndex: 2,
                      backgroundColor: "rgba(0,0,0,0.5)",
                      borderRadius: "4px",
                      display: "flex",
                      padding: "2px"
                    }}>
                    <CheckBox checked={isChecked} width={18} />
                  </Box>
                </Box>
              )
            })}
            {hasMore ? (
              <Box
                onClick={handleLoadMore}
                sx={{
                  flexShrink: 0,
                  width: 200,
                  height: 200,
                  cursor: loadingMore ? "default" : "pointer",
                  borderRadius: "12px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  color: theme.palette.secondary.main,
                  backgroundColor: theme.palette.background.main,
                  border: `2px dashed ${theme.palette.secondary.main}`
                }}>
                {loadingMore ? (
                  <CircularProgress size={28} sx={{ color: theme.palette.secondary.main }} />
                ) : (
                  <>
                    <AddIcon sx={{ fontSize: 32 }} />
                    <Typography variant="body2Regular">Show more</Typography>
                  </>
                )}
              </Box>
            ) : null}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mt: 2 }}>
            <AppButton
              styles={{
                height: "50px",
                padding: "0 16px",
                fontSize: "13px",
                whiteSpace: "nowrap",
                minWidth: "132px",
                justifyContent: "center"
              }}
              disabled={!selectedUrl || confirming}
              onClick={handleConfirm}>
              {confirming ? "Confirming..." : "Confirm"}
            </AppButton>
          </Box>
        </>
      ) : null}
    </Box>
  )
}

export default ImageSearchField
